/**
 * Frontend Cache Service
 * Manages client-side caching with KV backend integration
 * Provides sub-second page loads and offline-first capabilities
 */

export interface CacheConfig {
  ttl: number;
  prefetch?: boolean;
  critical?: boolean;
  compress?: boolean;
}

export interface CacheMetrics {
  hits: number;
  misses: number;
  hitRate: number;
  avgLoadTime: number;
  lastSync: Date;
}

export interface PrefetchStrategy {
  context: 'login' | 'dashboard' | 'learning' | 'preferences';
  patterns?: string[];
  priority: 'high' | 'medium' | 'low';
}

export class CacheService {
  private static instance: CacheService;
  private cache: Map<string, any> = new Map();
  private metrics: CacheMetrics = {
    hits: 0,
    misses: 0,
    hitRate: 0,
    avgLoadTime: 0,
    lastSync: new Date(),
  };

  private apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  private loadTimes: number[] = [];

  // Cache configurations by content type
  private static readonly CACHE_CONFIGS: Record<string, CacheConfig> = {
    session: { ttl: 86400, critical: true },
    preferences: { ttl: 604800, prefetch: true },
    dashboard: { ttl: 3600, prefetch: true, critical: true },
    progress: { ttl: 1800, prefetch: true },
    notifications: { ttl: 300, critical: true },
    courses: { ttl: 21600, compress: true },
    default: { ttl: 3600 },
  };

  private constructor() {
    // Initialize IndexedDB for persistent caching
    this.initIndexedDB();

    // Set up background sync
    this.setupBackgroundSync();
  }

  public static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  /**
   * Get cached data with automatic fallback
   */
  async get<T>(key: string, namespace: string = 'default', fetchFn?: () => Promise<T>): Promise<T | null> {
    const startTime = performance.now();
    const cacheKey = `${namespace}:${key}`;

    try {
      // Check memory cache first
      if (this.cache.has(cacheKey)) {
        const cached = this.cache.get(cacheKey);
        if (this.isValid(cached)) {
          this.recordHit(performance.now() - startTime);
          return cached.data;
        }
      }

      // Check IndexedDB
      const persistedData = await this.getFromIndexedDB(cacheKey);
      if (persistedData && this.isValid(persistedData)) {
        this.cache.set(cacheKey, persistedData);
        this.recordHit(performance.now() - startTime);
        return persistedData.data;
      }

      // Cache miss - fetch from KV backend
      const kvData = await this.fetchFromKV(namespace, key);
      if (kvData) {
        await this.set(key, namespace, kvData);
        this.recordHit(performance.now() - startTime);
        return kvData;
      }

      // Fallback to fetch function
      if (fetchFn) {
        this.recordMiss(performance.now() - startTime);
        const data = await fetchFn();
        await this.set(key, namespace, data);
        return data;
      }

      this.recordMiss(performance.now() - startTime);
      return null;
    } catch (error) {
      console.error('Cache get error:', error);
      this.recordMiss(performance.now() - startTime);

      if (fetchFn) {
        return await fetchFn();
      }
      return null;
    }
  }

  /**
   * Set cached data
   */
  async set<T>(key: string, namespace: string = 'default', data: T, config?: Partial<CacheConfig>): Promise<void> {
    const cacheKey = `${namespace}:${key}`;
    const cacheConfig = { ...CacheService.CACHE_CONFIGS[namespace], ...config };

    const cacheEntry = {
      data,
      metadata: {
        cachedAt: Date.now(),
        expiresAt: Date.now() + cacheConfig.ttl * 1000,
        namespace,
        critical: cacheConfig.critical,
        compressed: cacheConfig.compress,
      },
    };

    // Store in memory cache
    this.cache.set(cacheKey, cacheEntry);

    // Store in IndexedDB for persistence
    await this.saveToIndexedDB(cacheKey, cacheEntry);

    // Sync to KV backend if critical
    if (cacheConfig.critical) {
      await this.syncToKV(namespace, key, data);
    }
  }

  /**
   * Prefetch data based on user context
   */
  async prefetch(strategy: PrefetchStrategy): Promise<void> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/cache/prefetch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(strategy),
      });

      if (response.ok) {
        const result = await response.json();
        // Prefetch initiated with warming_id: result.warming_id

        // Fetch prefetched data after warming
        if (strategy.patterns) {
          await this.fetchPrefetchedData(strategy.patterns);
        }
      }
    } catch (error) {
      console.error('Prefetch error:', error);
    }
  }

  /**
   * Invalidate cached data
   */
  async invalidate(pattern: string, cascade: boolean = false): Promise<void> {
    // Clear memory cache
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));

    // Clear IndexedDB
    await this.clearIndexedDB(pattern);

    // Notify KV backend
    if (cascade) {
      await this.invalidateKV(pattern, cascade);
    }
  }

  /**
   * Get cache statistics
   */
  getMetrics(): CacheMetrics {
    return {
      ...this.metrics,
      hitRate: this.metrics.hits > 0 ? (this.metrics.hits / (this.metrics.hits + this.metrics.misses)) * 100 : 0,
    };
  }

  /**
   * Warm cache on login
   */
  async warmLoginCache(userId: string): Promise<void> {
    await this.prefetch({
      context: 'login',
      priority: 'high',
    });

    // Prefetch critical user data
    const criticalData = [
      this.get(`session:${userId}`, 'session'),
      this.get(`preferences:${userId}`, 'preferences'),
      this.get(`progress:${userId}`, 'progress'),
    ];

    await Promise.all(criticalData);
  }

  /**
   * Sync across devices
   */
  async syncDevices(userId: string, sourceDevice: string): Promise<void> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/cache/sync-devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({ userId, sourceDevice }),
      });

      if (response.ok) {
        const result = await response.json();
        // Successfully synced to devices: result.synced_devices
      }
    } catch (error) {
      console.error('Device sync error:', error);
    }
  }

  /**
   * Get bulk data efficiently
   */
  async getBulk<T>(namespace: string, keys: string[]): Promise<Map<string, T | null>> {
    const results = new Map<string, T | null>();

    // Check local cache first
    const uncachedKeys: string[] = [];
    keys.forEach(key => {
      const cacheKey = `${namespace}:${key}`;
      if (this.cache.has(cacheKey)) {
        const cached = this.cache.get(cacheKey);
        if (this.isValid(cached)) {
          results.set(key, cached.data);
        } else {
          uncachedKeys.push(key);
        }
      } else {
        uncachedKeys.push(key);
      }
    });

    // Fetch uncached keys from KV
    if (uncachedKeys.length > 0) {
      try {
        const response = await fetch(`${this.apiBaseUrl}/cache/bulk-get`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.getAuthToken()}`,
          },
          body: JSON.stringify({
            namespace,
            keys: uncachedKeys,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          Object.entries(data.results).forEach(([key, value]) => {
            results.set(key, value as T | null);
            if (value) {
              this.set(key, namespace, value);
            }
          });
        }
      } catch (error) {
        console.error('Bulk get error:', error);
      }
    }

    return results;
  }

  // Private helper methods

  private async initIndexedDB(): Promise<void> {
    if (!('indexedDB' in window)) return;

    const request = indexedDB.open('sigma08-cache', 1);

    request.onerror = () => {
      console.error('IndexedDB error');
    };

    request.onsuccess = () => {
      // IndexedDB initialized successfully
    };

    request.onupgradeneeded = event => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache', { keyPath: 'key' });
      }
    };
  }

  private async getFromIndexedDB(key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('sigma08-cache', 1);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['cache'], 'readonly');
        const store = transaction.objectStore('cache');
        const getRequest = store.get(key);

        getRequest.onsuccess = () => {
          resolve(getRequest.result?.value);
        };

        getRequest.onerror = () => {
          reject(getRequest.error);
        };
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  private async saveToIndexedDB(key: string, value: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('sigma08-cache', 1);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['cache'], 'readwrite');
        const store = transaction.objectStore('cache');
        const putRequest = store.put({ key, value });

        putRequest.onsuccess = () => {
          resolve();
        };

        putRequest.onerror = () => {
          reject(putRequest.error);
        };
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  private async clearIndexedDB(pattern: string): Promise<void> {
    // Implementation for clearing IndexedDB by pattern
  }

  private async fetchFromKV(namespace: string, key: string): Promise<any> {
    try {
      const endpoint = this.getKVEndpoint(namespace, key);
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }

      return null;
    } catch (error) {
      console.error('KV fetch error:', error);
      return null;
    }
  }

  private async syncToKV(namespace: string, key: string, data: any): Promise<void> {
    try {
      const endpoint = this.getKVEndpoint(namespace, key);
      await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('KV sync error:', error);
    }
  }

  private async invalidateKV(pattern: string, cascade: boolean): Promise<void> {
    try {
      await fetch(`${this.apiBaseUrl}/cache/invalidate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({ pattern, cascade }),
      });
    } catch (error) {
      console.error('KV invalidation error:', error);
    }
  }

  private async fetchPrefetchedData(patterns: string[]): Promise<void> {
    // Implementation for fetching prefetched data
  }

  private getKVEndpoint(namespace: string, key: string): string {
    const endpoints: Record<string, string> = {
      session: `/cache/session/${key}`,
      preferences: `/cache/preferences/${key}`,
      progress: `/cache/quick-access/${key}`,
      default: `/cache/quick-access/${key}`,
    };

    return `${this.apiBaseUrl}${endpoints[namespace] || endpoints.default}`;
  }

  private isValid(cached: any): boolean {
    if (!cached || !cached.metadata) return false;
    return Date.now() < cached.metadata.expiresAt;
  }

  private recordHit(loadTime: number): void {
    this.metrics.hits++;
    this.updateAvgLoadTime(loadTime);
  }

  private recordMiss(loadTime: number): void {
    this.metrics.misses++;
    this.updateAvgLoadTime(loadTime);
  }

  private updateAvgLoadTime(loadTime: number): void {
    this.loadTimes.push(loadTime);
    if (this.loadTimes.length > 100) {
      this.loadTimes.shift();
    }

    const sum = this.loadTimes.reduce((a, b) => a + b, 0);
    this.metrics.avgLoadTime = sum / this.loadTimes.length;
  }

  private getAuthToken(): string {
    // Import at the top of the file is better, but for now do dynamic import
    // to avoid circular dependency issues
    try {
      const token =
        localStorage.getItem('auth_token') ||
        localStorage.getItem('access_token') ||
        sessionStorage.getItem('auth_token') ||
        sessionStorage.getItem('access_token');

      if (!token) return '';

      // Try to decrypt if it's encrypted
      if (token && !token.includes('.')) {
        // Looks encrypted (no dots), decrypt it
        try {
          const decoded = atob(token);
          let result = '';
          const key = '5!gM@_0P7!mu$_K3y_2024_$3cur3';
          for (let i = 0; i < decoded.length; i++) {
            result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
          }
          return result;
        } catch {
          return token; // Return as-is if decryption fails
        }
      }

      return token;
    } catch {
      return '';
    }
  }

  private setupBackgroundSync(): void {
    // Set up periodic sync
    setInterval(() => {
      this.syncCriticalData();
    }, 60000); // Every minute
  }

  private async syncCriticalData(): Promise<void> {
    // Sync critical data to KV backend
    const criticalEntries: Array<[string, any]> = [];

    this.cache.forEach((value, key) => {
      if (value.metadata?.critical) {
        criticalEntries.push([key, value]);
      }
    });

    for (const [key, value] of criticalEntries) {
      const [namespace, ...keyParts] = key.split(':');
      await this.syncToKV(namespace, keyParts.join(':'), value.data);
    }

    this.metrics.lastSync = new Date();
  }
}

export default CacheService.getInstance();
